/**
 * CadencePanel.jsx — cold-outreach follow-up cadence tracker.
 *
 * Shows, for every BigDough upload list tagged with a list_name (first sent
 * this month or later — older one-off blasts are done and just clutter the
 * view):
 *   - the First Send date
 *   - all 4 follow-up stages, each as a status chip (sent/overdue/due soon/upcoming)
 *
 * Read-only — there's no "mark as sent" button here. A stage becomes "sent"
 * either by uploading the next BigDough file tagged with the same list_name,
 * or via a manual override set from the Calendar view (CadenceCalendar.jsx).
 * This panel is a status view over the same GET /api/cadence endpoint both
 * of those write paths feed into.
 */
import { useState, useEffect, useCallback } from 'react';
import { fetchCadence } from '../api/backend.js';

const STATUS_META = {
  sent:        { label: 'Sent',       bg: 'var(--green-bg, #ecfdf5)', color: 'var(--green, #059669)' },
  overdue:     { label: 'Overdue',    bg: 'var(--red-bg, #fef2f2)',   color: 'var(--red, #dc2626)' },
  due_soon:    { label: 'Due soon',   bg: 'var(--amber-bg, #fffbeb)', color: 'var(--amber, #d97706)' },
  upcoming:    { label: 'Upcoming',   bg: '#f1f5f9',                  color: '#64748b' },
  not_started: { label: 'Not started', bg: '#f8fafc',                color: '#cbd5e1' },
  not_tracked: { label: '—',          bg: '#f8fafc',                  color: '#cbd5e1' },
};

function fmtDate(d) {
  if (!d) return '—';
  const dt = new Date(d);
  if (isNaN(dt)) return '—';
  return dt.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
}

function StageChip({ stage }) {
  const meta = STATUS_META[stage.status] ?? STATUS_META.upcoming;
  const dateStr = stage.status === 'sent' ? fmtDate(stage.sentAt) : fmtDate(stage.dueDate);
  const title = stage.status === 'sent'
    ? `Sent ${dateStr}${stage.openRate != null ? ` — ${Math.round(stage.openRate * 100)}% open rate` : ''}`
    : stage.status === 'not_started' ? 'List not launched yet'
    : stage.status === 'not_tracked' ? 'One-off send — no follow-up expected'
    : `Due ${dateStr}`;
  return (
    <div
      title={title}
      style={{
        display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 2,
        padding: '4px 8px', borderRadius: 6, background: meta.bg, minWidth: 64,
      }}
    >
      <span style={{ fontSize: 10, fontWeight: 700, color: meta.color, textTransform: 'uppercase', letterSpacing: 0.3 }}>
        {meta.label}
      </span>
      <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>{dateStr}</span>
    </div>
  );
}

// "All lists" only shows lists first sent this month or later — older
// one-off blasts (conference-prep, specialist segments, etc.) are done and
// just clutter the view. Computed fresh each render, so it rolls forward
// with the calendar month instead of hardcoding a cutoff date.
function startOfCurrentMonth() {
  const now = new Date();
  return new Date(now.getFullYear(), now.getMonth(), 1);
}

export default function CadencePanel({ getToken }) {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [timelines, setTimelines] = useState([]);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await fetchCadence(getToken);
      setTimelines(res.timelines ?? []);
    } catch (e) {
      setError(e.message || 'Failed to load cadence data');
    } finally {
      setLoading(false);
    }
  }, [getToken]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return <div style={{ padding: 20, color: 'var(--text-3)', fontSize: 13 }}>Loading cadence…</div>;
  }
  if (error) {
    return <div style={{ padding: 20, color: 'var(--red, #dc2626)', fontSize: 13 }}>⚠ {error}</div>;
  }

  const cutoff = startOfCurrentMonth();
  const visibleTimelines = timelines.filter((t) => !t.firstSendAt || new Date(t.firstSendAt) >= cutoff);

  return (
    <div style={{ padding: '16px 20px', overflow: 'auto', flex: 1 }}>
      {/* Per-list timeline table */}
      <div style={{ fontSize: 13, fontWeight: 700, color: 'var(--navy)', marginBottom: 8 }}>
        All lists ({visibleTimelines.length})
      </div>
      {visibleTimelines.length === 0 ? (
        <div style={{ fontSize: 12.5, color: 'var(--text-3)' }}>
          No lists yet — tag a BigDough upload with a "List name" in Sync BigDough to start tracking one.
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {visibleTimelines.map((t) => (
            <div key={t.listName} style={{
              display: 'flex', alignItems: 'center', gap: 12, padding: '10px 12px',
              background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 8,
            }}>
              <div style={{ minWidth: 90 }}>
                <div style={{ fontWeight: 700, fontSize: 13.5, color: 'var(--navy)' }}>{t.listName}</div>
                <div style={{ fontSize: 10.5, color: 'var(--text-3)' }}>
                  {t.firstSendAt ? `First send ${fmtDate(t.firstSendAt)}` : 'Not launched'}
                </div>
                {!t.isTrackedCadence && (
                  <div style={{ fontSize: 10, color: 'var(--text-3)' }} title="Only 1 send so far and not a recognized G-number — not treated as an active multi-stage cadence yet">
                    one-off
                  </div>
                )}
              </div>
              <div style={{ display: 'flex', gap: 6, flexWrap: 'wrap' }}>
                {t.stages.map((s) => <StageChip key={s.stage} stage={s} />)}
              </div>
              {t.extraSends?.length > 0 && (
                <span style={{ fontSize: 10.5, color: 'var(--text-3)' }}>+{t.extraSends.length} extra send{t.extraSends.length !== 1 ? 's' : ''}</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
